import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useRef } from 'react';
import { CorridorPlace } from '../types';
import { ctrlWheelZoom } from './mapZoom';

interface Props {
  places: CorridorPlace[];
  /** Mesto koje operater trenutno gleda; crta se naglašeno. */
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** Čioda je prevučena — koordinata se ne upisuje dok se ne potvrdi. */
  onMove: (id: number, latitude: number, longitude: number) => void;
  /**
   * Klik na praznu mapu postavlja tačku izabranom mestu.
   *
   * Postoji zbog mesta koje je tek dodato na pravac: ono nema čiodu, pa se nema
   * šta prevući — a bez tačke ne može ni da se potvrdi.
   */
  onPlace?: (latitude: number, longitude: number) => void;
}

/** Zelena: potvrđeno. Žuta: predlog geokodera koji čeka čoveka. */
function pin(place: CorridorPlace, selected: boolean): L.DivIcon {
  const kind = place.isVerified ? 'ok' : 'pending';

  return L.divIcon({
    className: `cpin cpin--${kind}${selected ? ' cpin--selected' : ''}`,
    html: `<span>${place.sequence}</span>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12]
  });
}

/**
 * Mapa jednog pravca: linija kroz sva mesta, redom vožnje.
 *
 * Postoji zbog jedne vrste greške koju spisak ne pokazuje. Za „Stapar" je
 * geokoder vratio selo kod Valjeva — u spisku to izgleda kao svaki drugi red, a
 * na liniji je skok od dvesta kilometara koji se vidi bez čitanja.
 *
 * Zato se čiode i prevlače: ispravka je pomeranje tačke na pravi put, a ne
 * kucanje koordinata koje niko ne ume da proveri napamet.
 */
export default function CorridorMap({ places, selectedId, onSelect, onMove, onPlace }: Props) {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);

  // Leafletovi rukovaoci se vezuju jednom, pa moraju kroz referencu — inače bi
  // zauvek zvali prvu verziju funkcije.
  const select = useRef(onSelect);
  const move = useRef(onMove);
  const place = useRef(onPlace);

  useEffect(() => {
    select.current = onSelect;
    move.current = onMove;
    place.current = onPlace;
  }, [onSelect, onMove, onPlace]);

  useEffect(() => {
    if (!container.current || map.current) return;

    const instance = L.map(container.current, { scrollWheelZoom: false })
      .setView([45.77, 19.11], 9);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 18
    }).addTo(instance);

    instance.on('click', (event: L.LeafletMouseEvent) => {
      place.current?.(event.latlng.lat, event.latlng.lng);
    });

    map.current = instance;
    layer.current = L.layerGroup().addTo(instance);

    const odjavi = ctrlWheelZoom(instance, container.current);

    return () => {
      odjavi();
      instance.remove();
      map.current = null;
      layer.current = null;
    };
  }, []);

  useEffect(() => {
    if (!map.current || !layer.current) return;

    layer.current.clearLayers();

    const withPoint = places.filter(
      (p): p is CorridorPlace & { latitude: number; longitude: number } =>
        p.latitude !== null && p.longitude !== null
    );

    if (withPoint.length === 0) return;

    const line = withPoint.map((p) => [p.latitude, p.longitude] as [number, number]);

    if (line.length > 1) {
      L.polyline(line, { color: '#2b6cb0', weight: 3, opacity: 0.7 }).addTo(layer.current);
    }

    for (const place of withPoint) {
      const marker = L.marker([place.latitude, place.longitude], {
        icon: pin(place, place.id === selectedId),
        draggable: true
      })
        .bindTooltip(`${place.sequence}. ${place.placeName}`)
        .addTo(layer.current);

      marker.on('click', () => select.current(place.id));
      marker.on('dragstart', () => select.current(place.id));
      marker.on('dragend', () => {
        const { lat, lng } = marker.getLatLng();
        move.current(place.id, lat, lng);
      });
    }

    map.current.fitBounds(L.latLngBounds(line), { padding: [30, 30] });
  }, [places, selectedId]);

  return <div className="corridor__map" ref={container} />;
}
